import type { ExcelCellValue, ExcelExportColumn } from '@/lib/export-excel'
import { formatDateTime } from '@/lib/format'
import { waitingFor } from '@/lib/hr'
import { messages } from '@/lib/messages'
import type { HrIntervention, HrRequest, HrTask } from '@/services/http/hr'
import { actionLabel } from '@/features/hr/hr-actions'
import { requestSummary } from '@/features/hr/request-copy'

/** İK listelerinin Excel çıktısı: ekrandaki (süzülmüş) satırlar, metin olarak. */

const t = messages.hr
const r = t.approvals

export interface HrExport {
  columns: ExcelExportColumn[]
  rows: ExcelCellValue[][]
}

const headers = (labels: string[]): ExcelExportColumn[] => labels.map((header) => ({ header }))

const when = (value: string | null | undefined) => (value ? formatDateTime(value) : '')

export function requestsExport(items: HrRequest[]): HrExport {
  return {
    columns: headers([
      r.columns.personel,
      t.detail.fields.sicil,
      r.columns.firma,
      r.columns.tur,
      r.columns.tip,
      r.columns.zaman,
      r.columns.acan,
      t.filters.status,
      r.columns.guncelleme,
      r.columns.bekliyor,
      r.columns.dia,
      r.descriptionLabel,
    ]),
    rows: items.map((item) => {
      const summary = requestSummary(item)
      const open = item.status === 'onay_bekliyor' || item.status === 'revizyonda'
      return [
        item.personel_adi,
        item.sicilno,
        item.firma,
        r.kind[item.kind] ?? item.kind,
        summary.tip,
        summary.zaman,
        item.created_by.name,
        r.tabs[item.status] ?? item.status,
        when(item.updated_at),
        open ? waitingFor(item.updated_at) : '',
        item.action ? (t.actionStatus[item.action.status] ?? item.action.status) : '',
        item.description,
      ]
    }),
  }
}

export function tasksExport(items: HrTask[]): HrExport {
  const m = t.tasks
  return {
    columns: headers([
      m.columns.zaman,
      m.columns.kisi,
      t.detail.fields.sicil,
      m.columns.firma,
      t.detail.fields.sube,
      m.columns.kayit,
      m.columns.sorun,
      t.filters.category,
      t.detail.attempts,
      t.detail.error,
      m.columns.since,
    ]),
    rows: items.map((item) => [
      when(item.at),
      item.personel_adi,
      item.sicilno,
      item.firma,
      item.sube,
      `${t.interventions.target[item.target_kind] ?? item.target_kind} ${item.target_id}`,
      m.tabs[item.kind] ?? item.kind,
      item.kategori ? (t.category[item.kategori] ?? item.kategori) : '',
      item.attempts,
      item.message,
      when(item.since),
    ]),
  }
}

export function interventionsExport(items: HrIntervention[]): HrExport {
  const m = t.interventions
  return {
    columns: headers([
      m.columns.zaman,
      m.columns.mudahale,
      m.columns.kayit,
      m.record,
      r.columns.personel,
      m.columns.firma,
      m.columns.yapan,
      m.columns.onay,
      m.decidedBy,
      m.columns.sonuc,
      m.finishedAt,
    ]),
    rows: items.map((item) => [
      when(item.requested_at),
      actionLabel(item.source === 'talep' ? `talep_${item.type}` : item.type),
      m.target[item.target_kind] ?? item.target_kind,
      item.target_id,
      item.personel_adi ?? item.sicilno,
      item.firma,
      item.requested_by.name,
      item.direct
        ? m.direct
        : item.request_status
          ? (r.tabs[item.request_status] ?? item.request_status)
          : m.noApproval,
      item.decision?.actor.name,
      item.action_status ? (t.actionStatus[item.action_status] ?? item.action_status) : '',
      when(item.finished_at),
    ]),
  }
}
